import type { TimeBlock } from "./db";
import { dayStartMs } from "./time";

// Day-timeline math for the Plan screen (PL-4). Minutes are counted from
// local midnight in Africa/Kampala; storage stays UTC.

export const DAY_START_MIN = 6 * 60;
export const DAY_END_MIN = 23 * 60;
export const SLOT_MIN = 15;
export const DEFAULT_BLOCK_MIN = 30;

const MS_PER_MIN = 60_000;

export function snap(min: number): number {
	return Math.round(min / SLOT_MIN) * SLOT_MIN;
}

/** Minutes after local midnight of `day` for an ISO instant (may be <0 or >1440). */
export function minuteOfDay(iso: string, day: string): number {
	return Math.round((Date.parse(iso) - dayStartMs(day)) / MS_PER_MIN);
}

export function instantAt(day: string, minute: number): string {
	return new Date(dayStartMs(day) + minute * MS_PER_MIN).toISOString();
}

/**
 * Start and end for a new block. The start snaps to the grid and the block is
 * clamped to the visible day, so a 2-hour task dropped at 22:30 ends at 23:00.
 */
export function newBlockRange(
	day: string,
	startMin: number,
	durationMin: number = DEFAULT_BLOCK_MIN,
): { start_at: string; end_at: string } {
	const start = Math.min(Math.max(snap(startMin), DAY_START_MIN), DAY_END_MIN - SLOT_MIN);
	const end = Math.min(start + Math.max(snap(durationMin), SLOT_MIN), DAY_END_MIN);
	return { start_at: instantAt(day, start), end_at: instantAt(day, end) };
}

type Range = Pick<TimeBlock, "start_at" | "end_at">;

/** Shifts a block by `delta` minutes, keeping its length, inside the day. */
export function moveRange(block: Range, day: string, delta: number): Range {
	const start = minuteOfDay(block.start_at, day);
	const length = minuteOfDay(block.end_at, day) - start;
	const next = Math.min(Math.max(snap(start + delta), DAY_START_MIN), DAY_END_MIN - length);
	return { start_at: instantAt(day, next), end_at: instantAt(day, next + length) };
}

/** Moves a block so it starts at `startMin`, keeping its length. */
export function moveRangeTo(block: Range, day: string, startMin: number): Range {
	return moveRange(block, day, snap(startMin) - minuteOfDay(block.start_at, day));
}

/** Changes the end by `delta` minutes; a block is never shorter than one slot. */
export function resizeRange(block: Range, day: string, delta: number): Range {
	const start = minuteOfDay(block.start_at, day);
	const end = minuteOfDay(block.end_at, day);
	const next = Math.min(Math.max(snap(end + delta), start + SLOT_MIN), DAY_END_MIN);
	return { start_at: block.start_at, end_at: instantAt(day, next) };
}

/** Top offset and height (in minutes from DAY_START_MIN) for drawing a block. */
export function blockBox(block: Range, day: string): { top: number; height: number } | null {
	const start = Math.max(minuteOfDay(block.start_at, day), DAY_START_MIN);
	const end = Math.min(minuteOfDay(block.end_at, day), DAY_END_MIN);
	if (end <= start) return null;
	return { top: start - DAY_START_MIN, height: end - start };
}

export function formatClock(min: number): string {
	return `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;
}
